import { useQuery } from "@tanstack/react-query";
import { Activity, Clock3, Leaf, ShieldCheck, Trees } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { RetryButton, StatePanel } from "../components/StatePanel";
import { api, formatDate } from "../lib/api";

interface Dashboard {
  readonly metrics: Readonly<Record<string, number>>;
  readonly condition_distribution: readonly {
    readonly condition: string;
    readonly count: number;
  }[];
  readonly calculated_at: string;
}

export function OverviewPage() {
  const query = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api<Dashboard>("/admin/dashboard"),
  });
  if (query.isLoading) {
    return (
      <StatePanel
        kind="loading"
        title="Loading field record"
        detail="Collecting the latest requests, evidence, and review activity."
      />
    );
  }
  if (query.isError || query.data === undefined) {
    return (
      <StatePanel
        kind="error"
        title="The overview is unavailable"
        detail="No changes were made. Try loading the current record again."
        action={<RetryButton onClick={() => void query.refetch()} />}
      />
    );
  }
  const metrics = query.data.metrics;
  const cards = [
    {
      label: "Plants under care",
      value: metrics.declared_plant_quantity ?? 0,
      icon: Trees,
      note: `${(metrics.assignments ?? 0).toLocaleString()} assignments`,
    },
    {
      label: "Verified evidence",
      value: metrics.verified_posts ?? 0,
      icon: ShieldCheck,
      note: `${(metrics.original_posts ?? 0).toLocaleString()} originals`,
    },
    {
      label: "Awaiting review",
      value: metrics.review_backlog ?? 0,
      icon: Clock3,
      note: "Age and risk prioritized",
    },
    {
      label: "Verified points",
      value: metrics.verified_points ?? 0,
      icon: Leaf,
      note: `${(metrics.badge_awards ?? 0).toLocaleString()} badges earned`,
    },
  ] as const;
  return (
    <>
      <PageHeader
        eyebrow="Program overview"
        title="Field record"
        description="What has been assigned, documented, and verified across the current program scope."
      />
      <section className="metric-grid" aria-label="Program metrics">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <article className="metric" key={card.label}>
              <span className="metric-icon">
                <Icon aria-hidden="true" />
              </span>
              <p>{card.label}</p>
              <strong>{card.value.toLocaleString()}</strong>
              <small>{card.note}</small>
            </article>
          );
        })}
      </section>
      <section className="panel overview-grid">
        <div>
          <div className="section-heading">
            <div>
              <p className="eyebrow">Verified condition</p>
              <h2>Current evidence mix</h2>
            </div>
            <Activity aria-hidden="true" />
          </div>
          {query.data.condition_distribution.length === 0 ? (
            <p className="quiet">No verified condition evidence yet.</p>
          ) : (
            <div className="condition-list">
              {query.data.condition_distribution.map((item) => (
                <div key={item.condition}>
                  <span>{item.condition.replaceAll("_", " ")}</span>
                  <strong>{item.count.toLocaleString()}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="ledger-note">
          <p className="eyebrow">Record freshness</p>
          <h2>Calculated {formatDate(query.data.calculated_at)}</h2>
          <p>
            Evidence and next actions lead this view. Recognition totals follow
            verified decisions and never substitute for survival outcomes.
          </p>
        </div>
      </section>
    </>
  );
}
